import { Injectable } from '@angular/core';
import { LOCAL_STORAGE_KEY } from '@core/configs/local-storage-key.config';
import { Mode } from '@core/constants/enum.common';
import { normalizeSettingHeaders } from '@core/helper/check-sheet-setting-normalizer.helper';
import { Utils } from '@core/helper/utils.helper';
import { ExcreationConfigModel, NyuShoDisplayConfigModel } from '@core/models/models/common.model';
import { cloneDeep } from 'lodash';
import { UserDashboardSreenSetting } from '@modules/user-dashboard/user-dashboard-setting-dialog/user-dashboard-setting.model';
import { LocalStorageService } from './local-storage.service';
import { SessionContextService } from './session-context.service';

@Injectable({ providedIn: 'root' })
export class TvDefaultSettingConfigService {
  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly sessionContextService: SessionContextService,
  ) { }

  async initializeForCurrentOffice(): Promise<boolean> {
    return this.writeDefaultConfigForCurrentOffice(false);
  }

  /** Replaces the current office's TV display settings with the central defaults. */
  async resetForCurrentOffice(): Promise<boolean> {
    return this.writeDefaultConfigForCurrentOffice(true);
  }

  private async writeDefaultConfigForCurrentOffice(overwriteExisting: boolean): Promise<boolean> {
    if (Number(this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode)) !== Mode.TV) return false;

    const officeSelected = this.sessionContextService.getOfficeSelected<any>();
    const officeCd = officeSelected?.officeCd;
    if (!officeCd) return false;

    try {
      const savedSettings = this.localStorageService.getItem(LOCAL_STORAGE_KEY.settingDialog);
      const allSettings: Record<string, any> = savedSettings
        && typeof savedSettings === 'object'
        && !Array.isArray(savedSettings)
        ? savedSettings
        : {};
        
      if (!overwriteExisting && allSettings[officeCd] != null) return false;
      allSettings[officeCd] = await this.createDefaultConfig(Utils.isNyusho(officeSelected?.officeType));
      this.localStorageService.setItem(LOCAL_STORAGE_KEY.settingDialog, allSettings);
      return true;
    } catch (error) {
      console.error('Unable to initialize TV default setting config:', error);
      return false;
    }
  }

  private async createDefaultConfig(isNyuSho: boolean): Promise<Record<string, any>> {
    const configs = await this.loadScreenConfigs();
    const settingConfig: Record<string, any> = {
      headers: this.createNormalHeaders(isNyuSho, configs['normalHeaders']),
      timeChangePage: 30,
      bathingScreenConfig: new configs['LayoutConfig'](),
      excreationConfig: new ExcreationConfigModel(),
      userDashboardSettings: new UserDashboardSreenSetting(),
      isFlashingMoshiokuri: true,
    };

    if (isNyuSho) {
      settingConfig[LOCAL_STORAGE_KEY.nyuShoDisplayConfig] = new NyuShoDisplayConfigModel();
      settingConfig[LOCAL_STORAGE_KEY.settingDialogFunctionalTraining] = {
        headers: this.createFunctionalTrainingHeaders(configs['functionalTraining']),
        sortConfig: {
          isSortByRoom: configs[LOCAL_STORAGE_KEY.functionalTraining].defaultConfig.isSortByRoom,
          isSortByName: configs[LOCAL_STORAGE_KEY.functionalTraining].defaultConfig.isSortByName,
        },
      };
    } else {
      settingConfig[LOCAL_STORAGE_KEY.sortConfig] = {
        unitSelected: [],
        groupSelected: [],  
        hidden_overtime_flg: false,
        hidden_leave_user: false,
      };
      settingConfig[LOCAL_STORAGE_KEY.settingDialogShortTime] = {
        headers: this.createShortTimeHeaders(configs[LOCAL_STORAGE_KEY.shortTime]),
        sortConfig: cloneDeep(configs[LOCAL_STORAGE_KEY.shortTime].defaultSortConfig),
      };
    }

    return settingConfig;
  }

  private createNormalHeaders(isNyuSho: boolean, configHeaders: any[]): any[] {
    const headers = cloneDeep(configHeaders)
      .filter((header: any) => header?.field !== 'index')
      .map((header: any) => ({
        ...header,
        title: header.title ?? header.items,
        visible: header.visible ?? true,
      }));

    return isNyuSho ? this.addSnackMoistureHeader(headers) : this.addLeaveReasonHeader(headers);
  }

  private createFunctionalTrainingHeaders(config: any): any[] {
    return normalizeSettingHeaders(config.header, [], {
      excludedFields: ['index'],
      prepareHeader: header => this.prepareScreenHeader(header),
    }).headers;
  }

  private createShortTimeHeaders(config: any): any[] {
    return normalizeSettingHeaders(this.addLeaveReasonHeader(cloneDeep(config.header)), [], {
      excludedFields: ['index', ...config.excludedFields],
      prepareHeader: header => this.prepareScreenHeader(header),
    }).headers;
  }

  private prepareScreenHeader(header: any): any {
    const preparedHeader = {
      ...header,
      title: header.title ?? header.items,
      displayLbl: header.displayLbl ?? this.getHeaderDisplayText(header),
      visible: header.visible ?? true,
    };
    return preparedHeader;
  }

  private getHeaderDisplayText(header: any): string {
    if (!Array.isArray(header?.items) || header.items.length === 0) return header?.displayLbl ?? '';
    if (header.isSplitCell) return header.items.map((item: any) => item.title).join(' ');
    return header.items[0]?.title ?? '';
  }

  private addSnackMoistureHeader(headers: any[]): any[] {
    const snackHeader = headers.find(header => header.field === 'snack');
    if (!snackHeader || headers.some(header => header.field === 'snack_moisture')) return headers;

    headers.splice(headers.indexOf(snackHeader) + 1, 0, {
      width: '5%',
      items: [{ title: 'おやつ水分' }],
      title: 'おやつ水分',
      field: 'snack_moisture',
      headerName: 'おやつ水分',
      visible: snackHeader.hasSubHeader == null ? true : snackHeader.hasSubHeader,
    });
    return headers;
  }

  private addLeaveReasonHeader(headers: any[]): any[] {
    if (headers.some(header => header.field === 'leave_reason')) return headers;
    headers.push({ field: 'leave_reason', headerName: '中止理由', visible: true });
    return headers;
  }

  private async loadScreenConfigs(): Promise<Record<string, any>> {
    const [nyuShoModule, tsuShoModule, functionalTrainingModule, shortTimeModule, bathingSettingModule] = await Promise.all([
      import('@modules/check-sheet/nyusho/check-sheet-nyusho.config'),
      import('@modules/check-sheet/tsusho/check-sheet-tsusho.config'),
      import('@modules/check-sheet/functional-training/check-sheet-functional-training.config'),
      import('@modules/check-sheet/short-time/check-sheet-short-time.config'),
      import('@modules/bathing-schedule/bathing-monitor.setting.model'),
    ]);

    return {
      normalHeaders: Utils.isNyusho(this.sessionContextService.getOfficeSelected<any>()?.officeType)
        ? nyuShoModule.nyushoGridConfig.header
        : tsuShoModule.tsushoGridConfig.header,
      functionalTraining: functionalTrainingModule.functionalTrainingGridConfig,
      shortTime: {
        ...shortTimeModule.shortTimeGridConfig,
        excludedFields: shortTimeModule.shortTimeSettingDialogExcludedFields,
      },
      LayoutConfig: bathingSettingModule.LayoutConfig,
    };
  }

}
